import { useCallback, useState } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from '@/shared/hooks'
import { toast } from 'react-toastify'
import { FileText, Download, Plus, Loader2 } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useProjectWithVersions, useCreateVersion } from '../hooks'
import { ScopingHeader } from '../components'

/**
 * Generate a sanitized filename for PDF download
 * Format: [project-name]-specs-v[version].pdf
 */
function generatePdfFilename(projectName: string, version: number): string {
  const sanitized = projectName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `${sanitized}-specs-v${version}.pdf`
}

/**
 * Download a file from URL with custom filename
 */
async function downloadFile(url: string, filename: string): Promise<void> {
  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error('Failed to fetch file')
    }

    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = blobUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error('Download failed:', error)
    throw error
  }
}

/**
 * Post-Scoping Page - PDF ready for download
 * Design reference: Pencil Node ID kJGDD
 */
export function PostScopingPage() {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { shortId } = useParams<{ shortId: string }>()

  // Fetch project with versions
  // TODO: Update useProjectWithVersions to use shortId (SCOP2-007b)
  const { data: project, isLoading, error } = useProjectWithVersions(shortId)

  // Create version mutation
  const createVersionMutation = useCreateVersion()
  const [isCreatingVersion, setIsCreatingVersion] = useState(false)
  const [showAllVersions, setShowAllVersions] = useState(false)

  // Version history display config
  const MAX_VISIBLE_VERSIONS = 3

  // Derive data from project
  const projectName = project?.name || 'Project'
  const currentVersion = project?.current_version || 1
  const versions = project?.versions || []

  // Get current version's PDF URL and scoping data
  const currentVersionData = versions.find((v) => v.version_number === currentVersion)

  const handleBack = useCallback(() => {
    navigate('/dashboard')
  }, [navigate])

  const handleDownloadPdf = useCallback(
    async (version?: number) => {
      const targetVersion = version || currentVersion
      const versionData = versions.find((v) => v.version_number === targetVersion)

      if (!versionData?.pdf_url) {
        toast.error(t('postScoping.download.notAvailable'))
        return
      }

      const filename = generatePdfFilename(projectName, targetVersion)

      try {
        await downloadFile(versionData.pdf_url, filename)
        toast.success(t('postScoping.download.success'))
      } catch {
        toast.error(t('postScoping.download.error'))
      }
    },
    [currentVersion, versions, projectName, t]
  )

  const handleRequestNewVersion = useCallback(async () => {
    if (!project?.id || isCreatingVersion) return

    setIsCreatingVersion(true)

    try {
      const result = await createVersionMutation.mutateAsync({
        projectId: project.id,
        currentVersion,
        previousScopingData: currentVersionData?.scoping_data,
      })

      toast.success(
        t('postScoping.versions.createSuccess', {
          version: result.newVersionNumber,
        })
      )

      // Navigate to the scoping page for the new version (use shortId from URL)
      navigate(`/scoping/${shortId}`)
    } catch (error) {
      console.error('Failed to create new version:', error)
      toast.error(t('postScoping.versions.createError'))
    } finally {
      setIsCreatingVersion(false)
    }
  }, [
    project?.id,
    shortId,
    isCreatingVersion,
    currentVersion,
    currentVersionData?.scoping_data,
    createVersionMutation,
    navigate,
    t,
  ])

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-dvh items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-foreground-muted" />
      </div>
    )
  }

  // Error state
  if (error || !project) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-4 bg-background">
        <p className="text-foreground-muted">{t('postScoping.error.notFound')}</p>
        <button
          onClick={handleBack}
          className="text-accent hover:underline"
        >
          {t('postScoping.error.backToDashboard')}
        </button>
      </div>
    )
  }

  // Generate filename for display
  const pdfFileName = generatePdfFilename(projectName, currentVersion)

  return (
    <div className="flex h-dvh flex-col bg-background">
      {/* Header with contextual navigation */}
      <ScopingHeader
        projectName={projectName}
        version={currentVersion}
        badge={{
          label: t('postScoping.badge.completed'),
          variant: 'success',
        }}
        statusText={t('postScoping.header.version', { version: currentVersion })}
        onBack={handleBack}
        backLabel={t('postScoping.header.backToDashboard')}
      />

      {/* Main content - centered with secondary background */}
      <main className="flex flex-1 flex-col items-center overflow-y-auto bg-background-secondary px-8 py-12">
        <div className="flex w-full max-w-[600px] flex-col gap-12">
          {/* Download Card */}
          <div className="flex flex-col items-center gap-6 rounded-2xl border border-border bg-white p-10">
            {/* PDF Icon */}
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-[#FEE2E2]">
              <FileText className="h-10 w-10 text-[#DC2626]" />
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-foreground">
              {t('postScoping.download.title')}
            </h1>

            {/* Description */}
            <p className="text-center text-sm leading-relaxed text-foreground-muted">
              {t('postScoping.download.description')}
            </p>

            {/* Download button */}
            <button
              onClick={() => handleDownloadPdf()}
              disabled={!currentVersionData?.pdf_url}
              className={cn(
                'flex items-center gap-2.5 rounded-xl px-8 py-4 text-base font-semibold text-white transition-colors',
                currentVersionData?.pdf_url
                  ? 'bg-primary hover:bg-primary-hover'
                  : 'cursor-not-allowed bg-foreground-subtle'
              )}
            >
              <Download className="h-5 w-5" />
              {t('postScoping.download.button')}
            </button>

            {/* Filename or not available message */}
            <span className="text-xs text-foreground-subtle">
              {currentVersionData?.pdf_url
                ? pdfFileName
                : t('postScoping.download.generating')}
            </span>
          </div>

          {/* Version History Section */}
          {versions.length > 0 && (
            <div className="flex flex-col gap-4">
              {/* Section header */}
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-foreground">
                  {t('postScoping.versions.title')}
                </h2>
                <button
                  onClick={handleRequestNewVersion}
                  disabled={isCreatingVersion}
                  className={cn(
                    'flex items-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-white transition-colors',
                    isCreatingVersion
                      ? 'cursor-not-allowed opacity-70'
                      : 'hover:bg-accent-hover'
                  )}
                >
                  {isCreatingVersion ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plus className="h-3.5 w-3.5" />
                  )}
                  {isCreatingVersion
                    ? t('postScoping.versions.creating')
                    : t('postScoping.versions.requestNew', {
                        version: currentVersion + 1,
                      })}
                </button>
              </div>

              {/* Versions list */}
              <div className="flex flex-col gap-2">
                {(showAllVersions
                  ? versions
                  : versions.slice(0, MAX_VISIBLE_VERSIONS)
                ).map((v) => {
                  const isCurrent = v.version_number === currentVersion
                  const hasUrl = !!v.pdf_url
                  const isCompleted = v.status === 'completed'

                  return (
                    <div
                      key={v.version_number}
                      className={cn(
                        'flex items-center justify-between rounded-lg bg-white p-4',
                        isCurrent
                          ? 'border-2 border-accent'
                          : 'border border-border opacity-70'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {/* Version badge */}
                        <span
                          className={cn(
                            'flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold',
                            isCurrent
                              ? 'bg-accent text-white'
                              : 'bg-background-secondary text-foreground-muted'
                          )}
                        >
                          {v.version_number}
                        </span>
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground">
                              {t('postScoping.versions.versionLabel', {
                                version: v.version_number,
                              })}
                            </span>
                            {/* Status badge */}
                            <span
                              className={cn(
                                'rounded px-1.5 py-0.5 text-[10px] font-medium',
                                isCompleted
                                  ? 'bg-[#DCFCE7] text-[#166534]'
                                  : 'bg-[#FEF3C7] text-[#92400E]'
                              )}
                            >
                              {isCompleted
                                ? t('postScoping.versions.statusCompleted')
                                : t('postScoping.versions.statusScoping')}
                            </span>
                          </div>
                          <span className="text-xs text-foreground-muted">
                            {new Date(v.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      {/* Download icon */}
                      <button
                        onClick={() => handleDownloadPdf(v.version_number)}
                        disabled={!hasUrl}
                        className={cn(
                          'flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
                          hasUrl
                            ? 'hover:bg-background-secondary'
                            : 'cursor-not-allowed opacity-40'
                        )}
                        aria-label={t('postScoping.versions.download', {
                          version: v.version_number,
                        })}
                      >
                        <Download className="h-[18px] w-[18px] text-foreground-muted" />
                      </button>
                    </div>
                  )
                })}
              </div>

              {/* View all / Show less toggle */}
              {versions.length > MAX_VISIBLE_VERSIONS && (
                <button
                  onClick={() => setShowAllVersions(!showAllVersions)}
                  className="text-center text-sm font-medium text-accent hover:underline"
                >
                  {showAllVersions
                    ? t('postScoping.versions.showLess')
                    : t('postScoping.versions.viewAll', {
                        count: versions.length,
                      })}
                </button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
